'use client';

import {
  OrderProgressStage,
  OrderProgressStageState,
  OrderWorkProofInspectionSummary,
} from 'bff';
import { useTranslations } from 'i11n';
import React from 'react';
import { cn, TooltipProvider } from 'ui';
import {
  OrderProgressAudience,
  orderProgressStageTone,
} from '../constants/order-progress';
import { OrderProgressStep } from './OrderProgressStep';

/**
 * The stepper, horizontal from `sm` and a vertical list below: never a
 * horizontal scroll, hiding stages defeats reading the state at a glance.
 *
 * Purely presentational: the stages arrive already derived by the backend. That
 * includes WHICH stages exist — Reporte XOR Inspección means the backend sends
 * one proof stage or the other, so the step that does not apply is simply not
 * in the array and nothing here has to filter it out.
 */
export const OrderProgressStepper = ({
  stages,
  audience,
  basePath,
  activeStage,
  workProof,
  scheduledFor,
}: {
  stages: OrderProgressStageState[];
  audience: OrderProgressAudience;
  // Order detail path each stage link hangs from. Absent = tracking-only
  // rendering: the nodes are plain markers instead of links.
  basePath?: string;
  activeStage: OrderProgressStage | null;
  workProof: OrderWorkProofInspectionSummary | null;
  // Planned instant of the work (ISO 8601, UTC), for the scheduling node.
  scheduledFor: string | null;
}) => {
  const t = useTranslations('orders.progress');

  return (
    // One provider for the six nodes: moving between adjacent steps reopens the
    // detail without waiting out the delay again.
    <TooltipProvider delayDuration={100}>
      <ol
        aria-label={t('title')}
        className='flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-0'
      >
        {stages.map((stage, index) => (
          <React.Fragment key={stage.stage}>
            <OrderProgressStep
              state={stage}
              audience={audience}
              basePath={basePath}
              isCurrent={stage.stage === activeStage}
              workProof={workProof}
              scheduledFor={scheduledFor}
            />

            {/* The connector encodes reachability with its SHAPE, not only with
                color: solid between stages already gone through, dashed from
                there on. */}
            {index < stages.length - 1 && (
              <span
                aria-hidden
                className={cn(
                  'hidden flex-1 border-t-2 sm:mt-4 sm:block',
                  orderProgressStageTone(stage).connector,
                  stage.status === 'done' ? 'border-solid' : 'border-dashed',
                )}
              />
            )}
          </React.Fragment>
        ))}
      </ol>
    </TooltipProvider>
  );
};
